/**
 * How the notifications inbox writes a moment relative to now — the design's
 * "2m" / "1h" / "Yesterday" / "Mon" column, its "Today" / "Mon 8 Sep" day
 * headers, and the detail panel's "Today at 9:39 AM".
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parses the service's `created_at`. A timestamp without an offset is the
 * service's UTC clock, so it is read as UTC rather than as local time.
 */
export function parseCreatedAt(raw: string | undefined | null): Date | null {
  if (!raw) return null;
  const hasOffset = /([+-]\d{2}:?\d{2}|Z)$/.test(raw);
  const date = new Date(hasOffset ? raw : `${raw}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole calendar days from `moment` back to `now`; 0 today, 1 yesterday. */
export function daysAgo(moment: Date, now: Date): number {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const b = new Date(moment.getFullYear(), moment.getMonth(), moment.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

/** The row's right-hand column. */
export function formatRelative(moment: Date, now: Date = new Date()): string {
  const seconds = Math.floor((now.getTime() - moment.getTime()) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const days = daysAgo(moment, now);
  if (days <= 0) return `${Math.floor(minutes / 60)}h`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return WEEKDAYS[moment.getDay()];
  return dayMonth(moment, now);
}

/** The band above a day's rows. */
export function formatDayHeader(moment: Date, now: Date = new Date()): string {
  const days = daysAgo(moment, now);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (moment.getFullYear() === now.getFullYear()) {
    return `${WEEKDAYS[moment.getDay()]} ${dayMonth(moment, now)}`;
  }
  return dayMonth(moment, now);
}

/** The detail panel's timestamp: "Today at 9:39 AM". */
export function formatMoment(moment: Date, now: Date = new Date()): string {
  return `${formatDayHeader(moment, now)} at ${formatClock(moment)}`;
}

/** 12-hour clock, "9:39 AM". */
export function formatClock(moment: Date): string {
  const hour = moment.getHours() % 12 === 0 ? 12 : moment.getHours() % 12;
  const minute = String(moment.getMinutes()).padStart(2, '0');
  return `${hour}:${minute} ${moment.getHours() < 12 ? 'AM' : 'PM'}`;
}

function dayMonth(moment: Date, now: Date): string {
  const base = `${moment.getDate()} ${MONTHS[moment.getMonth()]}`;
  return moment.getFullYear() === now.getFullYear() ? base : `${base} ${moment.getFullYear()}`;
}

/** The web billing page; the one route whose query string is kept. */
export const BILLING_ROUTE = '/main/billing-and-subscription';

/** The billing tabs a notification may ask for; add-ons and overview are not deep-linked. */
export type BillingDeepLinkTab = 'billing-history' | 'payment-method';

/**
 * Where a notification's `payload.route` points. The route is already a web
 * dashboard path — the notification service writes it for this app — so it is
 * used as-is once it is confirmed to be an internal one.
 *
 * Query strings are dropped except on the billing page, where `tab` and
 * `invoiceId` are rebuilt so the page can open the right tab and scroll to the
 * receipt (FNF-592). The mobile app maps the same fields the same way.
 *
 * Returns null when there is nothing better to open than the inbox itself.
 */
export function dashboardRoute(payload: Record<string, string> | undefined): string | null {
  const route = payload?.['route'];
  if (typeof route !== 'string' || !route.startsWith('/main/')) return null;
  if (route === '/main/notifications') return null;
  const [rawPath, query = ''] = route.split('?');
  const path = rawPath.length > 1 && rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
  if (path !== BILLING_ROUTE) return path;

  const params = new URLSearchParams(query);
  const invoiceId =
    firstNonEmpty(payload?.['invoiceId'], payload?.['invoice_id'], params.get('invoiceId')) ??
    undefined;
  const tab = billingTabFor(firstNonEmpty(params.get('tab'), payload?.['tab']), invoiceId);
  return billingRoute(tab, invoiceId);
}

/** `/main/billing-and-subscription` with only the parameters the page reads. */
export function billingRoute(tab?: BillingDeepLinkTab, invoiceId?: string): string {
  const params = new URLSearchParams();
  if (tab) params.set('tab', tab);
  if (invoiceId) params.set('invoiceId', invoiceId);
  const query = params.toString();
  return query ? `${BILLING_ROUTE}?${query}` : BILLING_ROUTE;
}

/**
 * Normalises the tab a notification names. A receipt implies Billing History;
 * tabs that are not deep-linked (overview, add-ons) are dropped.
 */
export function billingTabFor(
  tab: string | null | undefined,
  invoiceId?: string,
): BillingDeepLinkTab | undefined {
  switch (tab) {
    case 'billing-history':
    case 'history':
      return 'billing-history';
    case 'payment-method':
      return 'payment-method';
  }
  return invoiceId ? 'billing-history' : undefined;
}

function firstNonEmpty(...values: (string | null | undefined)[]): string | null {
  return values.find((v) => typeof v === 'string' && v.length > 0) ?? null;
}

const ROUTE_LABELS: { prefix: string; label: string }[] = [
  { prefix: BILLING_ROUTE, label: 'Open billing' },
  { prefix: '/main/dashboard', label: 'Open dashboard' },
  { prefix: '/main/order-payment', label: 'Open order' },
  { prefix: '/main/order', label: 'Open orders' },
  { prefix: '/main/menu', label: 'Open menu' },
  { prefix: '/main/staffs', label: 'Open team' },
  { prefix: '/main/device', label: 'Open devices' },
  { prefix: '/main/report', label: 'Open reports' },
  { prefix: '/main/restaurant-profile', label: 'Open restaurant' },
];

/**
 * What the detail panel's primary button says for where it goes. A billing
 * route that names a receipt says so.
 */
export function routeActionLabel(route: string | null): string | null {
  if (!route) return null;
  const [path, query = ''] = route.split('?');
  if (path === BILLING_ROUTE && new URLSearchParams(query).has('invoiceId')) {
    return 'Open receipt';
  }
  return ROUTE_LABELS.find((r) => path.startsWith(r.prefix))?.label ?? 'Open';
}
